import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { ProjectPicker } from '../projects/ProjectPicker';
import { ToolbarContext } from "../../../_myb/layout/components/toolbar/Toolbar";
import { PaymentConfig, TxnConfig, getPaymentConfig } from "./TxnConfig";
import { AttrType, Contact, ContactType, DateFilter, NumberRanges, Payment, PaymentHeader, PaymentLines, Txn, TxnType, PaymentType, SystemAccountCode, BankTransactions, Account } from "../../types";
import { generateShortUUID } from "../misc_objects/Utils";
import { fetchAccounts, fetchContact, fetchFxRates, fetchTxnsWithFilters, fetchNumberRange, getNextNumber, getPayment, getReturnPayment, getTxn, upSertPayment } from "../../api";
import { Content } from "../../../_myb/layout/components/content";
import { ContactPicker } from "../masterdata/components/ContactPicker";
import NumberInput from "../widgets/components/NumberInput";
import Flatpickr from '../widgets/components/FlatpickrSafe';
import { AccountPicker } from "../masterdata/components/AccountPicker";
import { GenericPicker, MultiSelectPicker } from "../masterdata/components/AttributePicker";
import DiscountInput from "../widgets/components/DiscountInput";
import EditablePaymentItems from "./EditablePaymentItems";
import { toast } from "react-toastify";
import { scrollToFirstError } from "../utils/MiscFunctions";
import DOMPurify from "dompurify";
import { SaveDropdownButton } from "../widgets/ButtonGroup";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import { CustomPrompt } from "../widgets/components/ModalPrompts";
import { handlePrint } from "./ViewTxn";
import { CurrencyNumberOutput } from "../widgets/components/FormattedOutputs";
import { AccountType } from "../../Constants";
import DatePicker from "../widgets/components/DatePicker";
import { globalValue, useAppContext } from "../apps/AppContext";
import FileAttachment from "../widgets/components/FileAttachment";
import { fetchFile } from "../../api";
import { InlineEditField } from "../widgets/components/InlineEditField";
import { useFormDefaults } from "../widgets/hooks/useDefaults";
import { trackEvent, AnalyticsEvent } from '../apps/analytics';

type NameValuePair = {
    field: keyof PaymentHeader;
    value: string | number | boolean | Date;
};

interface PaymentFormProps {
    paymentType: PaymentType,
    paymentId?: string,
    returnTxnId?: string,
    bankTxn?: BankTransactions,
    bankAcct?: string,
    // Pre-selects this specific Invoice/Bill as the sole payment line, allocated in full —
    // used when opened from a confirmed bank-reconciliation auto-match suggestion, so the
    // suggested invoice is paid rather than whichever open invoice FIFO allocation would pick.
    initialTxnId?: string,
    onSave?: (payment: Payment) => void;
    onClose?: () => void;
}

export const PaymentForm: React.FC<PaymentFormProps> = ({ paymentType, paymentId, returnTxnId, bankAcct, bankTxn, initialTxnId, onSave, onClose }) => {
    const { updateToolbar } = useContext(ToolbarContext);
    const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | undefined>(undefined);
    const [contact, setContact] = useState<Contact>(new Contact());
    const navigate = useNavigate()
    const inputRef = useRef<HTMLInputElement>(null);
    const isReturnAlloc = Boolean(returnTxnId)
    const [isFormInitialized, setIsFormInitialized] = useState(false);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const isEditMode = !!paymentId;
    const { defaults, isLoadingDefaults, saveDefault, getDefault } = useFormDefaults(paymentType, isEditMode);
    useEffect(() => {
        const getAccounts = async () => {
            try {
                const fetchedAccounts: Account[] = await fetchAccounts();
                setAccounts(fetchedAccounts);
            } catch (error) {
                console.error('Error fetching accounts:', error);
            }
        };
        getAccounts();
    }, []);
    const getAccountBySystemCode = (sysAcctCode?: SystemAccountCode): string | undefined => {
        if (!sysAcctCode || !accounts || accounts.length === 0) return undefined;

        const account = accounts.find(acc => acc.systemAccountCode === sysAcctCode);
        return account?.accountId;
    };
    const defaultPaymentAcct = getAccountBySystemCode(SystemAccountCode.CASH);
    const setToolbarValues = () => {
        updateToolbar({
            title: 'New Toolbar Title',
        });
    };
    const roundToPointOne = (value: number) => {
        return Math.round(value * 10) / 10; // rounds to nearest 0.1
    };
    const [payment, setPayment] = useState<Payment>(new Payment({
        paymentHeader: new PaymentHeader({
            paymentId: generateShortUUID(),
            paymentType: paymentType,
            paymentAmt: (bankTxn) && bankTxn.deposit || bankTxn?.withdrawal,
            paymentAcct: (bankAcct) ? bankAcct : (bankTxn) && bankTxn.bankAcct,
            reference: (bankTxn) && bankTxn?.reference || bankTxn?.detail,
            paymentDate: (bankTxn) ? bankTxn.txnDate : new Date(),
        }),
        paymentLines: []
    }));
    const [prevPayment, setPrevPayment] = useState<Payment>()
    // Amount, account and date are locked whenever the payment is linked to a bank
    // transaction (whether opened directly from the bank screen or edited later from the
    // Payment List) — editing any of these would create inconsistent bank reconciliations.
    const isCategorizedFromBank = Boolean(bankTxn);
    const isCategorized = isCategorizedFromBank || Boolean(payment.paymentHeader.bankTxnId);
    const { bizMaster, bizSettings } = useAppContext();
    const [isWithHoldingTaxEdit, setIsWithHoldingTaxEdit] = useState<boolean>(!!payment.paymentHeader.withHoldingAmt);
    const [isOtherChargesEdit, setIsOtherChargesEdit] = useState<boolean>(!!payment.paymentHeader.otherChargesAmt);
    const [isCashRefund, setIsCashRefund] = useState<boolean>(!!payment.paymentHeader.cashRefundAmt);
    const [isDiscountEdit, setIsDiscountEdit] = useState<boolean>(!!payment.paymentHeader.discountAmt);
    const [isContactChanged, setIsContactChanged] = useState<boolean>(false)
    const [isSavingPayment, setIsSavingPayment] = useState<boolean>(false)
    const [contactTxns, setContactTxns] = useState<Txn[]>([])
    const [totalDue, setTotalDue] = useState<number>(0)
    const [editFxRate, setEditFxRate] = useState(false);
    const [triggerFxRate, setTriggerFxRate] = useState(false);
    const [remainingAmt, setRemainingAmt] = useState<number>(0)
    const [errors, setErrors] = useState<{ [key: string]: string }>({})
    const [numberRangeForEdit, setNumberRangeForEdit] = useState<NumberRanges | undefined>()
    const prevContactIdRef = useRef<string | undefined>(undefined);
    const [file, setFile] = useState<File | undefined>(undefined);
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus(); // Set focus to the input field
        }
        if ((paymentType === PaymentType.PurchaseReturnAllocation || paymentType === PaymentType.SalesReturnAllocation) && (!returnTxnId)) {
            toast.error('Error: Invalid Return Transaction'.toTranslatedDisplay())
        }
    }, []);

    const updateFx = async () => {
        if (contact?.currId && contact.currId !== bizMaster?.bizDetails?.currency) {
            const fxRate = await fetchFxRates(contact.currId, payment.paymentHeader.paymentDate)
            setPayment((prevPayment) => ({
                ...prevPayment,
                paymentHeader: {
                    ...prevPayment.paymentHeader,
                    currencyId: contact.currId,
                    fxRateAmt: fxRate
                }
            }))
        } else {
            setPayment((prevPayment) => ({
                ...prevPayment,
                paymentHeader: {
                    ...prevPayment.paymentHeader,
                    currencyId: contact.currId,
                    fxRateAmt: undefined
                }
            }))
        }
    }
    useEffect(() => {
        updateFx()
    }, [triggerFxRate])

    useEffect(() => {
        const getContactDetails = async () => {
            if (payment.paymentHeader.contactId) {
                const fetchedContact = await fetchContact(payment.paymentHeader.contactId)

                setContact(fetchedContact.contact)
                const dateFilter = new DateFilter({ predefinedDateFilter: 'AllTime' });
                const fetchedTxns = await fetchTxnsWithFilters(dateFilter, paymentConfig?.contactTxnType, undefined,
                    {
                        contactId: payment.paymentHeader.contactId
                    })
                const fetchedBalanceTxns = await fetchTxnsWithFilters(dateFilter, paymentConfig?.openingBalanceTxnType, undefined,
                    {
                        contactId: payment.paymentHeader.contactId
                    })
                setContactTxns([...fetchedTxns, ...fetchedBalanceTxns])
                const hasContactChanged = prevContactIdRef.current !== undefined &&
                    prevContactIdRef.current !== payment.paymentHeader.contactId;

                convertToPaymentLines([...fetchedTxns, ...fetchedBalanceTxns], hasContactChanged)
                prevContactIdRef.current = payment.paymentHeader.contactId;
            }
            setTriggerFxRate(!triggerFxRate)
        }
        getContactDetails()
    }, [payment.paymentHeader.contactId, paymentConfig])

    useEffect(() => {
        const fetchPayment = async () => {
            if (accounts.length === 0) return; // wait until accounts are loaded
            setIsFormInitialized(false);
            if (paymentId || bankTxn?.objectId) {
                const fetchedPayment = await getPayment(paymentId || bankTxn?.objectId || '');
                if (fetchedPayment) {
                    setPayment(fetchedPayment)
                    setPrevPayment(fetchedPayment)
                    if (fetchedPayment.paymentHeader.attachmentId) {
                        const blob = await fetchFile(fetchedPayment.paymentHeader.attachmentId);
                        if (blob) {
                            const fetchedFile = new File([blob], fetchedPayment.paymentHeader.attachmentId, { type: blob.type });
                            setFile(fetchedFile);
                        }
                    }
                }
            } else if (returnTxnId) {
                try {
                    const fetchedReturnTxn = await getTxn(returnTxnId);

                    if (fetchedReturnTxn) {
                        let returnPayment: Payment | null = null;
                        try {
                            returnPayment = await getReturnPayment(returnTxnId);
                        } catch (error) {
                            console.error('Error fetching return payment:', error);
                        }

                        const updatedPayment = returnPayment ?? { ...payment }; // Use returnPayment if available, otherwise fallback to payment

                        // Update paymentHeader with fetched txn data
                        updatedPayment.paymentHeader = {
                            ...updatedPayment.paymentHeader,
                            returnTxnId: returnTxnId,
                            contactId: fetchedReturnTxn.txnHeader.contactId,
                            paymentAmt: (fetchedReturnTxn.txnHeader?.totalAmt || 0) - (fetchedReturnTxn.txnHeader?.paymentAmt || 0)
                        };

                        // Generate payment number if it's not already set
                        if (!updatedPayment.paymentHeader.paymentNo) {
                            updatedPayment.paymentHeader.paymentNo = await getNextNumber(paymentType);
                        }
                        // Update payment state
                        setPayment(updatedPayment);
                    }
                } catch (error) {
                    console.error('Error fetching return transaction:', error);
                }
            } else if (initialTxnId) {
                // Confirmed from a bank-reconciliation auto-match suggestion: pre-select this
                // exact invoice/bill as the sole payment line, allocated in full (paymentAmt is
                // already set from bankTxn above and matches its balanceDue — that's the
                // suggestion's match condition). Seeding contactId also triggers the
                // getContactDetails effect below, which fetches the contact's other open
                // invoices/bills — this line's preset allocAmt keeps its priority so they
                // receive no allocation as long as the full amount is already spoken for here.
                try {
                    const nextNo = await getNextNumber(paymentType);
                    const matchedTxn = await getTxn(initialTxnId);
                    setPayment(prev => ({
                        ...prev,
                        paymentHeader: {
                            ...prev.paymentHeader,
                            paymentNo: prev.paymentHeader.paymentNo || nextNo,
                            contactId: matchedTxn.txnHeader.contactId,
                            contactName: matchedTxn.txnHeader.contactName,
                        },
                        paymentLines: [new PaymentLines({
                            txnId: matchedTxn.txnHeader.txnId,
                            txnNo: matchedTxn.txnHeader.txnNo,
                            txnDate: matchedTxn.txnHeader.txnDate,
                            dueAmt: matchedTxn.txnHeader.balanceDue,
                            totalAmt: matchedTxn.txnHeader.totalAmt,
                            allocAmt: matchedTxn.txnHeader.balanceDue,
                        })],
                    }));
                } catch (error) {
                    console.error('Error fetching matched invoice/bill:', error);
                }
            } else {
                let nextNo = await getNextNumber(paymentType);
                const paymentAcct = bankAcct ? bankAcct : defaults.paymentAcct || getAccountBySystemCode(SystemAccountCode.CASH)
                setPayment(prev => ({
                    ...prev,
                    paymentHeader: {
                        ...prev.paymentHeader,
                        // only set paymentNo if it's not already there
                        paymentNo: prev.paymentHeader.paymentNo || nextNo,
                        paymentAcct: paymentAcct
                    }
                }));
            }
            setIsFormInitialized(true)
        }
        // Get the transaction configuration based on the txnType
        const config = getPaymentConfig(paymentType);
        setPaymentConfig(config);
        setToolbarValues()
        fetchNumberRange(paymentType).then(setNumberRangeForEdit);
        fetchPayment();
    }, [paymentType, defaults, accounts]); // Empty dependency array to ensure it runs only once

    useEffect(() => {
        const getTotalDue = (Array.isArray(contactTxns) ? contactTxns : [])
            .map(txn => txn.txnHeader?.balanceDue || 0)
            .reduce((acc, totalAmt) => acc + totalAmt, 0);
        setTotalDue(getTotalDue);
    }, [contactTxns, payment.paymentHeader.contactId]);

    useEffect(() => {
        setIsCashRefund(!!payment.paymentHeader.cashRefundAmt)
        setIsOtherChargesEdit(!!payment.paymentHeader.otherChargesAmt || !!payment.paymentHeader.otherChargesAcct)
        setIsWithHoldingTaxEdit(!!payment.paymentHeader.withHoldingAmt || !!payment.paymentHeader.withHoldingTaxAcct)
        validateForm()
    }, [payment]);

    useEffect(() => {
        const totalAlloc = (payment.paymentLines || []).reduce((sum, l) => sum + (l.allocAmt || 0), 0);
        const total = payment.paymentHeader.totalAmt || 0;
        setRemainingAmt(Math.max(0, Math.round((total - totalAlloc) * 100) / 100));
    }, [payment.paymentLines, payment.paymentHeader.totalAmt]);

    const validateForm = () => {
        if (isReturnAlloc) {
            const totalAllocAmt = (payment.paymentLines?.reduce((sum, line) => sum + (line.allocAmt || 0), 0)) || 0;
            const totalAmount = (payment.paymentHeader?.cashRefundAmt || 0) + totalAllocAmt;

            if (totalAmount <= 0) {
                setError('Empty Allocation', 'Error Cannot save without allocation or refund'.toTranslatedDisplay())
            } else {
                removeError('Empty Allocation')
            }
        }

        if (payment.paymentHeader.paymentAmt === 0 || (!payment.paymentHeader.paymentAmt)) {
            setError('Payment Amount', 'Error Cannot save with zero payment amount'.toTranslatedDisplay())
        } else {
            removeError('Payment Amount')
        }

        if (!payment.paymentHeader.contactId) {
            const partyLabel = payment.paymentHeader.paymentType === PaymentType.VendorPayment ? 'Vendor' : 'Customer';
            setError(partyLabel, `${partyLabel} is required!`.toTranslatedDisplay())
        } else {
            removeError('Customer')
            removeError('Vendor')
        }
    }

    const calculateTotalAmount = (paymentHeader: PaymentHeader) => {
        return Number(paymentHeader.paymentAmt || 0)
            + Number(paymentHeader.discountAmt || 0)
            + Number(paymentHeader.withHoldingAmt || 0)
            + Number(paymentHeader.otherChargesAmt || 0);
    }

    const allocatePaymentLines = (payment: Payment, totalAmt: number, updateAllocation: boolean) => {
        if (totalAmt > 0) {
            let remainingAmt = totalAmt;

            const newPaymentLines = payment.paymentLines?.map(paymentLine => {
                const prevPaymentLine = prevPayment?.paymentLines?.find(pl => pl.txnId === paymentLine.txnId)
                let allocAmt = paymentLine.allocAmt ?? 0;
                const dueAmt = (paymentLine?.dueAmt || 0) + (prevPaymentLine?.allocAmt || 0);

                const amountToAllocate = Math.min(remainingAmt, dueAmt);

                allocAmt = (updateAllocation) ? amountToAllocate : allocAmt;

                remainingAmt -= allocAmt;
                if ( roundToPointOne(remainingAmt)  < 0) {
                    setError('Remaining Amount', 'Allocated amount greater than total amount !'.toTranslatedDisplay() + remainingAmt)
                } else {
                    removeError('Remaining Amount')
                }
                const roundedAllocAmt = Math.round(allocAmt * 100) / 100
                return { ...paymentLine, allocAmt: roundedAllocAmt };
            });
            return {
                ...payment,
                paymentHeader: {
                    ...payment.paymentHeader,
                    totalAmt: totalAmt
                },
                paymentLines: newPaymentLines
            };
        }

        return payment; // Return the original payment object if totalAmt is not greater than 0
    }

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [htmlContent, setHtmlContent] = useState('');

    const setError = (key: string, errorMessage: string) => {
        setErrors((prevErrors) => ({
            ...prevErrors,
            [key]: errorMessage,
        }));
    };

    const removeError = (key: string) => {
        setErrors((prevErrors) => {
            const { [key]: removedError, ...restErrors } = prevErrors;
            return restErrors;
        });
    };

    const handleInputChange = <T = string | number | boolean | Date>(
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | null,
        field?: keyof PaymentHeader | NameValuePair[],
        value?: T | T[],
        isDiscountPct?: boolean
    ) => {
        setIsFormDirty(true);

        setPayment(prevPayment => {
            let newPaymentHeader: PaymentHeader = { ...prevPayment.paymentHeader };

            if (e) {
                const { name, value } = e.target;
                const key = name as keyof PaymentHeader;
                (newPaymentHeader as any)[key] = value;
            } else if (Array.isArray(field)) {
                field.forEach(({ field, value }) => {
                    (newPaymentHeader as any)[field] = value;
                });
                if (isDiscountPct !== undefined) {
                    newPaymentHeader.isDiscountPct = isDiscountPct;
                }
            } else if (field) {
                (newPaymentHeader as any)[field] = value;
                if (isDiscountPct !== undefined) {
                    newPaymentHeader.isDiscountPct = isDiscountPct;
                }
            }

            // 👇 always preserve existing paymentNo
            if (!newPaymentHeader.paymentNo && prevPayment.paymentHeader.paymentNo) {
                newPaymentHeader.paymentNo = prevPayment.paymentHeader.paymentNo;
            }

            const updatedPayment = { ...prevPayment, paymentHeader: newPaymentHeader };
            const totalAmt = calculateTotalAmount(newPaymentHeader);
            const newPayment = allocatePaymentLines(updatedPayment, totalAmt, true);
            newPayment.paymentHeader.totalAmt = totalAmt;

            return newPayment;
        });
    };

    const [confirmExit, setConfirmExit] = useState(false);
    const [isFormDirty, setIsFormDirty] = useState(false);
    const handleExit = () => {
        if (!isFormDirty) {
            if (onClose) {
                onClose()
            } else {
                navigate(-1)
            }
            setConfirmExit(true)
        }
    }

    const getPaymentCreateRoute = (type: PaymentType): string => {
        if (type === PaymentType.VendorPayment) return '/transactions/create-vendor-payment';
        return '/transactions/create-customer-payment';
    };

    const handleSubmit = async (event: React.FormEvent, toPrint?: boolean, toSend?: boolean, toAddNew?: boolean) => {
        if (numberRangeForEdit?.isDisabled && !payment.paymentHeader.paymentNo?.trim()) {
            toast.error('Payment Number is required!'.toTranslatedDisplay());
            setError('paymentNo', 'Payment Number is required!'.toTranslatedDisplay());
            // MBAI-28: scroll to payment number field
            scrollToFirstError('paymentNo');
            return;
        }
        removeError('paymentNo');
        if (Object.keys(errors).length > 0) {
            Object.keys(errors).forEach((key) => {
                // Combine the key and error message
                toast.error(`${key.toTranslatedDisplay()}: ${errors[key]}`);
            });
        } else {
            event.preventDefault();
            try {
                setIsSavingPayment(true)
                payment.paymentLines =
                    payment.paymentLines?.filter(line => line.allocAmt != null && line.allocAmt !== 0 && line.allocAmt !== undefined);

                const response = await upSertPayment(payment, file);
                trackEvent(AnalyticsEvent.PaymentRecorded);
                toast.success('Transaction upserted successfully!'.toTranslatedDisplay());
                if (onSave) onSave(payment)
                const sanitizedHtml = DOMPurify.sanitize(response, { USE_PROFILES: { html: true } });
                setHtmlContent(sanitizedHtml);
                setIsSubmitted(true);
                if (toPrint) {
                    await handlePrint(sanitizedHtml)
                }
                setIsSavingPayment(false)
                if (toAddNew) {
                    navigate(getPaymentCreateRoute(payment.paymentHeader.paymentType as PaymentType));
                } else if (toSend) {
                    navigate(`/transactions/send-payment/${payment.paymentHeader.paymentType}/${payment.paymentHeader.paymentId}`, { replace: true });
                } else {
                    if (!bankTxn) {
                        navigate(`/transactions/view-payment/${payment.paymentHeader.paymentType}/${payment.paymentHeader.paymentId}`, { replace: true });
                    } else if (onClose) {
                        onClose();
                    }
                }
            } catch (error: any) {
                setIsSavingPayment(false)
                console.error('Error upserting transaction:', error);
                toast.error(error.message);
            }
        }
    };

    const convertToPaymentLines = (txns: Txn[], clearExisting: boolean = false) => {
        const paymentLines: PaymentLines[] = clearExisting ? [] : (payment.paymentLines || [])
        // Sort transactions from oldest to newest by transaction date

        const sortedTxns = [...txns].sort((a, b) => {
            const dateA = a.txnHeader?.txnDate ? new Date(a.txnHeader.txnDate).getTime() : 0;
            const dateB = b.txnHeader?.txnDate ? new Date(b.txnHeader.txnDate).getTime() : 0;
            return dateA - dateB; // Oldest first
        });
        if (Array.isArray(sortedTxns)) {
            sortedTxns.forEach(txn => {
                const txnId = txn.txnHeader?.txnId;
                // Find the existing paymentLine if it exists
                const existingLineIndex = paymentLines.findIndex(line => line.txnId === txnId);

                if (existingLineIndex > -1) {
                    // Always update dueAmt for existing lines (even if balanceDue = 0),
                    // so allocatePaymentLines gets the correct current balance and doesn't over-allocate
                    paymentLines[existingLineIndex] = {
                        ...paymentLines[existingLineIndex], // Keep existing allocAmt
                        txnNo: txn.txnHeader?.txnNo,
                        txnDate: txn.txnHeader?.txnDate,
                        dueAmt: txn.txnHeader?.balanceDue,
                        totalAmt: txn.txnHeader?.totalAmt
                    };
                } else if ((txn?.txnHeader?.balanceDue || 0) > 0) {
                    // Only add new paymentLine if there is an outstanding balance
                    paymentLines.push(new PaymentLines({
                        txnId: txnId,
                        txnNo: txn.txnHeader?.txnNo,
                        txnDate: txn.txnHeader?.txnDate,
                        dueAmt: txn.txnHeader?.balanceDue,
                        totalAmt: txn.txnHeader?.totalAmt
                    }));
                }
            });
        }
        const newPayment = { ...payment, paymentLines: paymentLines }
        const totalAmt = calculateTotalAmount(newPayment.paymentHeader)
        // Always auto-allocate: existing lines are capped by dueAmt_effective (= prevAllocAmt for
        // fully-paid invoices), so their amounts are preserved. New lines with outstanding balance
        // then receive any surplus, consistent with what handleInputChange does.
        const updatedPayment = allocatePaymentLines(newPayment, totalAmt, true)
        setPayment(updatedPayment);
    }

    const handleWithHoldingTaxToggle = () => {
        setIsWithHoldingTaxEdit(!isWithHoldingTaxEdit);
    };
    const handleOtherChargesToggle = () => {
        setIsOtherChargesEdit(!isOtherChargesEdit);
    };
    const handleCashRefundToggle = () => {
        setIsCashRefund(!isCashRefund)
    };
    const handleDiscountToggle = () => {
        setIsDiscountEdit(!isDiscountEdit);
    };
    const contactChangeTrigger = () => {
        setIsContactChanged(!isContactChanged);
    };
    if (isSubmitted && !bankTxn) {
        navigate(`/transactions/view-payment/${paymentType}/${payment.paymentHeader.paymentId}`, { replace: true })
    }
    else {
        return (
            <Content>
                <div className='card mb-5 mb-xl-10' id='kt_profile_details_view'>
                    <div className='card-header cursor-pointer'>
                        <div className='card-title m-0 align-baseline'>
                            <h3 className='fw-bolder m-0'>{paymentConfig?.title?.toTranslatedDisplay()}</h3>
                            {numberRangeForEdit?.isDisabled ? (
                                <>
                                    <input
                                        type="text"
                                        name="paymentNo"
                                        className={`form-control form-control-sm ms-2 ${errors.paymentNo ? 'is-invalid' : ''}`}
                                        style={{ width: 'auto', minWidth: '140px' }}
                                        value={payment.paymentHeader.paymentNo || ''}
                                        placeholder={`${'Enter'.toTranslatedDisplay()} ${paymentConfig?.title?.toTranslatedDisplay()} ${'No'.toTranslatedDisplay()}`}
                                        onChange={(e) => {
                                            handleInputChange(e);
                                            removeError('paymentNo');
                                        }}
                                    />
                                    {errors.paymentNo && <div className="invalid-feedback d-block">{errors.paymentNo}</div>}
                                </>
                            ) : (
                                <InlineEditField
                                    value={payment.paymentHeader.paymentNo || ''}
                                    name="paymentNo"
                                    onChange={handleInputChange}
                                    placeholder={`${paymentConfig?.title?.toTranslatedDisplay()} ${'No'.toTranslatedDisplay()}`}
                                    displayComponent={
                                        <h4 className='fw-bolder ms-1 text-primary'>
                                            {payment.paymentHeader.paymentNo}
                                        </h4>
                                    }
                                    containerClassName="d-inline-flex"
                                />
                            )}
                        </div>
                        <div className='card-toolbar'>
                            <SaveDropdownButton
                                isLoading={isSavingPayment}
                                enableShortcuts
                                onSave={handleSubmit}
                                onSaveAndSend={(event) => handleSubmit(event, false, true)}
                                onSaveAndPrint={(event) => handleSubmit(event, true)}
                                onSaveAndAddNew={(event) => handleSubmit(event, false, false, true)}
                            />
                        </div>
                    </div>
                    {isEditMode && isCategorized && (
                        <div className="alert alert-warning d-flex align-items-center gap-3 mx-9 mt-3 mb-0 py-2">
                            <i className="bi bi-bank fs-4" />
                            <div>
                                {'This payment is already linked to a bank transaction. The amount, date, and account cannot be edited here.'.toTranslatedDisplay()}
                            </div>
                        </div>
                    )}
                    <div className='card-body sp-9'>
                        <form>
                            <div className="row mb-7">
                                <div className="col-md-3">
                                    <ContactPicker
                                        ref={inputRef}
                                        isDisabled={isReturnAlloc}
                                        selectedContact={payment.paymentHeader.contactId || payment.paymentHeader.contactName}
                                        contactType={paymentConfig?.contactType || ContactType.Customer}
                                        tabIndex={0}
                                        onContactChange={(contactId, contactInfo) => {
                                            handleInputChange(null, [
                                                { field: "contactId", value: contactInfo?.contactId || "" },
                                                { field: "contactName", value: contactInfo?.contactName || "" }
                                            ]);
                                            contactChangeTrigger();
                                        }}
                                    />
                                    {contact.billingAddress &&
                                        <>
                                            <label className="form-label mt-3">{'Billing Address'.toTranslatedDisplay()}</label>
                                            <span className="badge badge-light-secondary text-start" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                                                {contact.billingAddress}
                                            </span>
                                        </>
                                    }
                                    <span className="badge badge-light-secondary mt-3">
                                        <span style={{ fontSize: '1.25em', fontWeight: 'bold' }}>{'Total Due :'.toTranslatedDisplay()}</span>
                                        <span className="ms-1" style={{ fontSize: '1.25em', fontWeight: 'bold' }}>
                                            <CurrencyNumberOutput currencyId={payment.paymentHeader.currencyId} amount={totalDue} />
                                        </span>

                                    </span>
                                    <div className="mb-3">
                                        <FileAttachment
                                            label={'Attachments'.toTranslatedDisplay()}
                                            existingFile={file}
                                            onFileSelection={(selectedFile: File) => {
                                                setFile(selectedFile);
                                            }}
                                        />
                                    </div>

                                    {payment.paymentHeader.fxRateAmt && payment.paymentHeader.fxRateAmt !== 0 &&
                                        <>
                                            {!editFxRate ?
                                                <div className='my-3'>
                                                    <span onClick={() => setEditFxRate(true)} className='cursor-pointer badget badge-light p-3 fw-bolder'>1 {payment.paymentHeader.currencyId} = {payment.paymentHeader.fxRateAmt.toFixed(4)} {bizMaster?.bizDetails?.currency} </span>
                                                </div>
                                                :
                                                <div>
                                                    <NumberInput value={payment.paymentHeader.fxRateAmt}
                                                        name='fxRateAmt'
                                                        onChange={(e, parsedValue) => handleInputChange(e, 'fxRateAmt', parsedValue)}
                                                    />
                                                </div>
                                            }
                                        </>
                                    }

                                </div>
                                <div className="col-md-9">
                                    <div className="row mb-3">
                                        <div className="col-md-4 mb-3">
                                            <NumberInput
                                                name={"paymentAmt"}
                                                value={payment.paymentHeader.paymentAmt}
                                                label={'Payment Amount'.toTranslatedDisplay()}
                                                showCurrency
                                                currId={payment.paymentHeader.currencyId}
                                                inputDisabled={isReturnAlloc || isCategorized}
                                                onChange={(event, parsedValue) => handleInputChange(event, 'paymentAmt', parsedValue)}
                                            />

                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">{'Paymemt date'.toTranslatedDisplay()}</label>
                                            <DatePicker
                                                value={payment.paymentHeader.paymentDate}
                                                onChange={(date) => handleInputChange(null, 'paymentDate', date)}
                                                disabled={isCategorized}
                                                className='form-control'
                                                placeholder={'Transaction Date'.toTranslatedDisplay()}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">{'Reference'.toTranslatedDisplay()}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder={'Note/Reference'.toTranslatedDisplay()}
                                                name="reference"
                                                value={payment.paymentHeader.reference || ''}
                                                onChange={handleInputChange}
                                            />
                                        </div>

                                        {bizSettings?.projectSettings?.enabled && (
                                            <div className="col-md-4 mb-3">
                                                <label className="form-label">{'Project'.toTranslatedDisplay()}</label>
                                                <ProjectPicker
                                                    selectedProjectId={payment.paymentHeader.projectId}
                                                    clientId={payment.paymentHeader.contactId}
                                                    onProjectChange={(id) => handleInputChange(null, 'projectId', id ?? null)}
                                                />
                                            </div>
                                        )}

                                        {isReturnAlloc ? <>
                                            {isCashRefund ? (
                                                <>
                                                    <div className="col-md-4 mb-3">
                                                        <AccountPicker tabIndex={4}
                                                            label={'Cash Paid Account'.toTranslatedDisplay()}
                                                            selectedAccounts={[payment.paymentHeader.cashRefundAcct || '']}
                                                            accountType={AccountType.Assets}
                                                            onAccountChange={function (accountId: string | string[]): void {
                                                                handleInputChange(null, 'cashRefundAcct', accountId)
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <NumberInput
                                                            name="cashRefundAmt"
                                                            label={'Refund Amount'.toTranslatedDisplay()}
                                                            currId={payment.paymentHeader.currencyId}
                                                            value={payment.paymentHeader.cashRefundAmt}
                                                            onChange={(event, parsedValue) => handleInputChange(event, 'cashRefundAmt', parsedValue)}
                                                            placeholder={'0.00'.toTranslatedDisplay()}
                                                        />
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <GenericPicker
                                                            categoryType={AttrType.PaymentMode}
                                                            selectedAttribute={payment.paymentHeader.paymentMode}
                                                            onAttributeChange={(attributeId) => handleInputChange(null, 'paymentMode', attributeId)}
                                                            showColor={false}
                                                            label={'Payment Mode'.toTranslatedDisplay()}
                                                            tabIndex={2}
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="col-md-4 mb-3">
                                                        <div className="text mt-6">
                                                            <h5 className="text-primary" onClick={handleCashRefundToggle}>
                                                                + Refund Cash
                                                            </h5>
                                                        </div>
                                                    </div>

                                                </>
                                            )}


                                        </> :
                                            <>
                                                <div className="col-md-4 mb-3">
                                                    <AccountPicker
                                                        tabIndex={4}
                                                        label={paymentConfig?.paymentAccountTitle}
                                                        selectedAccounts={[payment.paymentHeader.paymentAcct || '']}
                                                        sysAcctCode={SystemAccountCode.CASH}
                                                        accountType={AccountType.Assets}
                                                        isDisabled={isCategorized}
                                                        onAccountChange={(accountId: string | string[]) => {
                                                            handleInputChange(null, 'paymentAcct', accountId)
                                                            if (!isEditMode && accountId) {
                                                                saveDefault('paymentAcct', accountId);
                                                            }
                                                        }
                                                        }
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <GenericPicker
                                                        categoryType={AttrType.PaymentMode}
                                                        selectedAttribute={payment.paymentHeader.paymentMode}
                                                        onAttributeChange={(attributeId) => handleInputChange(null, 'paymentMode', attributeId)}
                                                        showColor={false}
                                                        label={'Payment Mode'.toTranslatedDisplay()}
                                                        tabIndex={2}
                                                    />
                                                </div>

                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">{'Discount'.toTranslatedDisplay()}</label>
                                                    <DiscountInput
                                                        name="discountAmt"
                                                        currencyId={payment.paymentHeader.currencyId}
                                                        value={payment.paymentHeader.isDiscountPct ? (payment.paymentHeader.discountPct || 0) : payment.paymentHeader.discountAmt || 0}
                                                        isDiscountPct={payment.paymentHeader.isDiscountPct || false}
                                                        onChange={(value, isDiscountPct) => {
                                                            if (isDiscountPct) {
                                                                handleInputChange(null, 'discountPct', value, isDiscountPct);
                                                            } else {
                                                                handleInputChange(null, 'discountAmt', value, isDiscountPct);
                                                            }
                                                        }} />
                                                </div>
                                                {isWithHoldingTaxEdit ? (
                                                    <>
                                                        <div className="col-md-4 mb-3">
                                                            <AccountPicker tabIndex={4}
                                                                selectedAccounts={[payment.paymentHeader.withHoldingTaxAcct || '']}
                                                                label={'WithHolding Tax'.toTranslatedDisplay()}
                                                                sysAcctCode='TDS'
                                                                onAccountChange={function (accountId: string | string[]): void {
                                                                    handleInputChange(null, 'withHoldingTaxAcct', accountId)
                                                                }} />
                                                        </div>
                                                        <div className="col-md-4 mb-3">
                                                            <NumberInput
                                                                name="withHoldingAmt"
                                                                label={'WithHolding Amount'.toTranslatedDisplay()}
                                                                currId={payment.paymentHeader.currencyId}
                                                                value={payment.paymentHeader.withHoldingAmt}
                                                                onChange={(event, parsedValue) => handleInputChange(event, 'withHoldingAmt', parsedValue)}
                                                                placeholder={'0.00'.toTranslatedDisplay()}
                                                            />
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="col-md-4 mb-3">
                                                            <div className="text mt-6">
                                                                <h5 className="text-primary" onClick={handleWithHoldingTaxToggle}>
                                                                    + Add WithHolding/TDS Tax
                                                                </h5>
                                                            </div>
                                                        </div>

                                                    </>
                                                )}
                                                {isOtherChargesEdit ? (
                                                    <>
                                                        <div className="col-md-4 mb-3">
                                                            <AccountPicker tabIndex={4}
                                                                label={'Other Charges Tax'.toTranslatedDisplay()}
                                                                selectedAccounts={[payment.paymentHeader.otherChargesAcct || '']}
                                                                accountType={AccountType.Expense}
                                                                onAccountChange={function (accountId: string | string[]): void {
                                                                    handleInputChange(null, 'otherChargesAcct', accountId)
                                                                }} />
                                                        </div>
                                                        <div className="col-md-4 mb-3">
                                                            <NumberInput
                                                                name="otherChargesAmt"
                                                                label={'Other Charges Amount'.toTranslatedDisplay()}
                                                                value={payment.paymentHeader.otherChargesAmt}
                                                                currId={payment.paymentHeader.currencyId}
                                                                onChange={(event, parsedValue) => handleInputChange(event, 'otherChargesAmt', parsedValue)}
                                                                placeholder={'0.00'.toTranslatedDisplay()}
                                                            />
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="col-md-4 mb-3">
                                                            <div className="text mt-6">
                                                                <h5 className="text-primary" onClick={handleOtherChargesToggle}>
                                                                    + Add Other Charges
                                                                </h5>
                                                            </div>
                                                        </div>

                                                    </>
                                                )}
                                                <div className="col-md-4 mb-3">
                                                    <MultiSelectPicker
                                                        categoryType={AttrType.Tags}
                                                        label={'Tags'.toTranslatedDisplay()}
                                                        showColor={true}
                                                        selectedAttributes={payment.paymentHeader.tags}
                                                        onAttributeChange={(attributeIds: string[]) => handleInputChange(null, 'tags', attributeIds)}
                                                    />
                                                </div>
                                            </>}

                                    </div>
                                </div>
                            </div>
                            <EditablePaymentItems contact={contact} paymentConfig={paymentConfig} paymentLiness={payment.paymentLines || []}
                                hasErrors={(isError) => {
                                    if (isError) {
                                        setError('Allocated Amount', 'Error in Allocated amounts'.toTranslatedDisplay())
                                    } else {
                                        removeError('Allocated Amount')
                                    }
                                }}
                                setPaymentLiness={(paymentLines) => {
                                    setPayment(prevPayment => {
                                        // Update the paymentHeader first
                                        const updatedPayment = {
                                            ...prevPayment,
                                            paymentLines: paymentLines
                                        };

                                        // Calculate total amount based on the updated payment header
                                        const totalAmt = calculateTotalAmount(updatedPayment.paymentHeader);

                                        // Call allocatePaymentLines to allocate the amounts based on the updated header
                                        const newPayment = allocatePaymentLines(updatedPayment, totalAmt, false);
                                        return newPayment; // Return the updated payment state
                                    });

                                }} />
                            {remainingAmt > 0 && (
                                <div className="d-flex justify-content-end align-items-center mb-4 mt-2">
                                    <span className="text-muted me-3">{'Unallocated Amount:'.toTranslatedDisplay()}</span>
                                    <span className="badge badge-light-warning fs-5 px-4 py-2">
                                        <CurrencyNumberOutput amount={remainingAmt} currencyId={payment.paymentHeader.currencyId} />
                                    </span>
                                </div>
                            )}
                            <SaveDropdownButton
                                isLoading={isSavingPayment}
                                onSave={handleSubmit}
                                onSaveAndSend={(event) => handleSubmit(event, false, true)}
                                onSaveAndPrint={(event) => handleSubmit(event, true)}
                                onSaveAndAddNew={(event) => handleSubmit(event, false, false, true)}
                            />
                            <Button onClick={() => setConfirmExit(true)} className='ms-2 btn btn-secondary'>
                                {'Cancel'.toTranslatedDisplay()}
                            </Button>
                        </form>
                    </div>
                </div>
                <CustomPrompt
                    isVisible={confirmExit}
                    onClose={() => { setConfirmExit(false) }}
                    onConfirm={() => {
                        if (onClose) {
                            onClose()
                        } else {
                            navigate(-1)
                        }
                    }}
                    message={'You have unsaved changes. Do you really want to leave?'.toTranslatedDisplay()}
                    title={'Unsaved Changes'.toTranslatedDisplay()}
                    PrimaryText={'OK'.toTranslatedDisplay()}
                    secondaryText={'Cancel'.toTranslatedDisplay()}
                />
            </Content>
        )
    }
}

interface QuickPayFormProps {
    paymentType: PaymentType,
    paymentId?: string,
    txnId?: string,
    advanceTxnId?: string;
    contactId?: string;
    onSave?: (payment: Payment) => void;
    onClose?: () => void
}

export const QuickPayForm: React.FC<QuickPayFormProps> = ({ paymentType, paymentId, txnId, advanceTxnId, contactId, onSave, onClose }) => {
    const [txn, setTxn] = useState<Txn>()
    const [isSavingPayment, setIsSavingPayment] = useState<boolean>(false)
    const [isFormInitialized, setIsFormInitialized] = useState(false);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const isEditMode = !!paymentId;
    const { defaults, isLoadingDefaults, saveDefault, getDefault } = useFormDefaults(paymentType, isEditMode);
    const getAccountBySystemCode = (sysAcctCode?: SystemAccountCode): string | undefined => {
        if (!sysAcctCode || !accounts || accounts.length === 0) return undefined;

        const account = accounts.find(acc => acc.systemAccountCode === sysAcctCode);
        return account?.accountId;
    };
    useEffect(() => {
        const getAccounts = async () => {
            try {
                const fetchedAccounts: Account[] = await fetchAccounts();
                setAccounts(fetchedAccounts);
            } catch (error) {
                console.error('Error fetching accounts:', error);
            }
        };
        getAccounts();
    }, []);

    const [payment, setPayment] = useState<Payment>(new Payment({
        paymentHeader: new PaymentHeader({
            paymentId: generateShortUUID(),
            paymentType: paymentType,
            paymentDate: new Date(),
            ...(advanceTxnId && { advanceTxnId, isAdvancePayment: true }),
            ...(contactId && { contactId }),
        }),
        paymentLines: []
    }));
    const [errors, setErrors] = useState<{ [key: string]: string }>({})

    const getDetails = useCallback(async () => {
        if (accounts.length === 0) return; // wait until accounts are loaded
        setIsFormInitialized(false);
        if (advanceTxnId && !txnId) {
            // Advance payment mode: pre-fill payment number and contact
            const [nextPaymentNo, contactData] = await Promise.all([
                getNextNumber(paymentType),
                contactId ? fetchContact(contactId).catch(() => null) : Promise.resolve(null),
            ]);
            setPayment(prev => ({
                ...prev,
                paymentHeader: {
                    ...prev.paymentHeader,
                    paymentNo: prev.paymentHeader.paymentNo || nextPaymentNo,
                    paymentAcct: defaults.paymentAcct || getAccountBySystemCode(SystemAccountCode.CASH),
                    ...(contactData?.contact && {
                        contactId: contactData.contact.contactId,
                        contactName: contactData.contact.contactName ?? contactData.contact.companyName,
                    }),
                },
            }));
            setIsFormInitialized(true);
            return;
        }
        if (txnId) {
            const txnResult = await getTxn(txnId);

            const nextPaymentNo = await getNextNumber(paymentType);

            setPayment(prevPayment => {
                const newPaymentHeader = {
                    ...prevPayment.paymentHeader,
                    paymentAmt: txnResult.txnHeader.balanceDue,
                    totalAmt: txnResult.txnHeader.balanceDue || 0,
                    contactId: txnResult.txnHeader.contactId,
                    contactName: txnResult.txnHeader.contactName,
                    paymentAcct: defaults.paymentAcct || getAccountBySystemCode(SystemAccountCode.CASH),
                    paymentNo: prevPayment.paymentHeader.paymentNo || nextPaymentNo
                };

                const paymentLine = new PaymentLines({
                    txnId,
                    txnNo: txnResult.txnHeader?.txnNo,
                    txnDate: txnResult.txnHeader?.txnDate,
                    dueAmt: txnResult.txnHeader?.balanceDue,
                    totalAmt: txnResult.txnHeader?.totalAmt,
                    allocAmt: newPaymentHeader.totalAmt,
                });

                return {
                    ...prevPayment,
                    paymentHeader: newPaymentHeader,
                    paymentLines: [paymentLine],
                };
            });

            setTxn(txnResult);
        } else if (paymentId) {
            const paymentResult = await getPayment(paymentId);
            setPayment(paymentResult);
        }
        setIsFormInitialized(true);
    }, [txnId, paymentId, paymentType, defaults, accounts]);


    useEffect(() => {
        getDetails();
    }, [getDetails]);

    useEffect(() => {

        allocatePaymentLines(payment, payment.paymentHeader.totalAmt, true);
    }, [payment]);


    const calculateTotalAmount = (paymentHeader: PaymentHeader) => {
        return Number(paymentHeader.paymentAmt || 0)
            + Number(paymentHeader.discountAmt || 0)
            + Number(paymentHeader.withHoldingAmt || 0)
            + Number(paymentHeader.otherChargesAmt || 0);
    }

    const allocatePaymentLines = (payment: Payment, totalAmt: number, updateAllocation: boolean) => {
        if (totalAmt > 0) {
            let remainingAmt = totalAmt;

            const newPaymentLines = payment.paymentLines?.map(paymentLine => {
                let allocAmt = paymentLine.allocAmt ?? 0;
                const dueAmt = paymentLine.dueAmt ?? 0;

                const amountToAllocate = Math.min(remainingAmt, dueAmt);

                allocAmt = Math.round((updateAllocation ? amountToAllocate : allocAmt) * 100) / 100;
                remainingAmt -= allocAmt;
                paymentLine.allocAmt = allocAmt;
                if (remainingAmt < 0) {
                    // setErrors('Remaining Amount', 'Allocated amount greater than total amount!')
                } else {
                    // removeError('Remaining Amount')
                }
                return { ...paymentLine, allocAmt };
            });
            return {
                ...payment,
                paymentHeader: {
                    ...payment.paymentHeader,
                    totalAmt: totalAmt
                },
                paymentLines: newPaymentLines
            };
        }

        return payment; // Return the original payment object if totalAmt is not greater than 0
    }


    const handleInputChange = <T = string | number | boolean | Date>(
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | null,
        field?: keyof PaymentHeader | NameValuePair[],
        value?: T | T[],
        isDiscountPct?: boolean
    ) => {

        let newPaymentHeader: PaymentHeader = { ...payment.paymentHeader };
        if (e) {
            const { name, value } = e.target;

            newPaymentHeader = {
                ...payment.paymentHeader,
                [name]: value
            };

        } else if (Array.isArray(field)) {
            field.forEach(({ field, value }) => {
                newPaymentHeader = {
                    ...newPaymentHeader,
                    [field]: value
                };
            });
            if (isDiscountPct !== undefined) {
                newPaymentHeader = {
                    ...newPaymentHeader,
                    isDiscountPct: isDiscountPct
                };
            }
        } else if (field) {
            newPaymentHeader = {
                ...payment.paymentHeader,
                [field]: value,
                ...(isDiscountPct !== undefined && { isDiscountPct })
            };
        }

        setPayment(prevPayment => {
            // Update the paymentHeader first
            const updatedPayment = {
                ...prevPayment,
                paymentHeader: newPaymentHeader
            };

            // Calculate total amount based on the updated payment header
            const totalAmt = calculateTotalAmount(updatedPayment.paymentHeader);

            // Call allocatePaymentLines to allocate the amounts based on the updated header
            // const newPayment = allocatePaymentLines(updatedPayment, totalAmt, true);
            updatedPayment.paymentHeader.totalAmt = totalAmt
            return updatedPayment; // Return the updated payment state
        });
    }
    const handleSubmit = async (event: React.FormEvent, toPrint?: boolean, toSend?: boolean) => {
        if (Object.keys(errors).length > 0) {
            Object.keys(errors).forEach((key) => {
                // Combine the key and error message
                toast.error(`${key.toTranslatedDisplay()}: ${errors[key]}`);
            });
        } else {
            event.preventDefault();
            try {
                setIsSavingPayment(true)
                payment.paymentLines =
                    payment.paymentLines?.filter(line => line.allocAmt != null && line.allocAmt !== 0 && line.allocAmt !== undefined);
                const response = await upSertPayment(payment);
                trackEvent(AnalyticsEvent.PaymentRecorded);
                toast.success('Transaction upserted successfully!'.toTranslatedDisplay());
                if (onSave) onSave(payment)
                const sanitizedHtml = DOMPurify.sanitize(response, { USE_PROFILES: { html: true } });
                // setIsSubmitted(true);
                if (toPrint) {
                    await handlePrint(sanitizedHtml)
                }
                setIsSavingPayment(false)
                if (toSend) {
                    if (onSave) onSave(payment)
                } else {
                    if (onSave) onSave(payment)
                }
            } catch (error: any) {
                setIsSavingPayment(false)
                toast.error(`${'Error:'.toTranslatedDisplay()} ${error.message}`);
            }
        }
    };

    return (<>
        {advanceTxnId && (
            <div className="alert alert-light-primary border border-primary rounded d-flex align-items-center gap-3 mb-4 p-3">
                <i className="bi bi-cash-coin text-primary fs-4" />
                <div>
                    <div className="fw-bold text-primary fs-7">{'Advance Payment'.toTranslatedDisplay()}</div>
                    <div className="text-muted fs-8">{'This payment will be recorded as an advance against the Sales Order and auto-allocated when converted to an Invoice.'.toTranslatedDisplay()}</div>
                </div>
            </div>
        )}
        <div className="row">
            <div className="col-md-4 mb-3">
                <NumberInput
                    name={"paymentAmt"}
                    value={payment.paymentHeader.paymentAmt}
                    label={'Payment Amount'.toTranslatedDisplay()}
                    showCurrency
                    currId={payment.paymentHeader.currencyId}
                    onChange={(event, parsedValue) => handleInputChange(event, 'paymentAmt', parsedValue)}
                />

            </div>
            <div className="col-md-4 mb-3">
                <label className="form-label">{'Payment date'.toTranslatedDisplay()}</label>
                <DatePicker
                    value={payment.paymentHeader.paymentDate}
                    onChange={(date) => {
                        handleInputChange(null, 'paymentDate', date)
                    }}
                    className='form-control'
                    placeholder={'Transaction Date'.toTranslatedDisplay()}
                />
            </div>
            <div className="col-md-4 mb-3">
                <AccountPicker
                    tabIndex={4}
                    label={'Payment Account'.toTranslatedDisplay()}
                    selectedAccounts={[payment.paymentHeader.paymentAcct || '']}
                    defaultAccounts={isEditMode ? undefined : [getDefault('paymentAcct')].filter(Boolean)}
                    sysAcctCode={SystemAccountCode.CASH}
                    accountType={AccountType.Assets}
                    onAccountChange={(accountId: string | string[]) => {
                        const selectedAcctId = Array.isArray(accountId) ? accountId[0] : accountId;
                        handleInputChange(null, 'paymentAcct', selectedAcctId);
                        // Auto-save as default when user selects an account (only in create mode)
                        if (!isEditMode && selectedAcctId) {
                            saveDefault('paymentAcct', selectedAcctId);
                        }
                    }}
                />
            </div>
            <div className="col-md-4 mb-3">
                <GenericPicker
                    categoryType={AttrType.PaymentMode}
                    selectedAttribute={payment.paymentHeader.paymentMode}
                    onAttributeChange={(attributeId) => handleInputChange(null, 'paymentMode', attributeId)}
                    showColor={false}
                    label={'Payment Mode'.toTranslatedDisplay()}
                    tabIndex={2}
                />
            </div>
        </div>
        <SaveDropdownButton
            isLoading={isSavingPayment}
            onSave={handleSubmit}
            onSaveAndSend={(event) => handleSubmit(event, false, true)}
            onSaveAndPrint={(event) => handleSubmit(event, true)}
        />
        <Button onClick={() => {
            if (onClose) onClose()
        }} className='ms-2 btn btn-secondary'>
            {'Cancel'.toTranslatedDisplay()}
        </Button>

    </>)

}